import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui'
import { useBillingStatus } from '@/hooks/billing/useBillingStatus'

export function BillingStatusBadge() {
  const { t } = useTranslation()
  const { data: billingStatus, isLoading } = useBillingStatus()

  if (isLoading) {
    return <Badge variant="outline">{t('billing.badge.loading')}</Badge>
  }

  if (!billingStatus) {
    return null
  }

  if (billingStatus.isExempt) {
    const reasonKey = billingStatus.exemptReason as keyof typeof t
    const reason =
      reasonKey &&
      t(`billing.badge.reasons.${reasonKey}`, { defaultValue: t('billing.badge.exemptSpecial') })

    return (
      <Badge
        variant="default"
        className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-200 dark:border-green-700"
      >
        ✨ {t('billing.badge.exempt', { reason })}
      </Badge>
    )
  }

  // Handle past due status
  if (billingStatus.pastDue) {
    return (
      <Badge
        variant="destructive"
        className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900 dark:text-red-200 dark:border-red-700"
      >
        ⚠️ {t('billing.badge.pastDue')}
      </Badge>
    )
  }

  // Handle canceled subscription
  if (billingStatus.canceled) {
    if (billingStatus.hasAccess && billingStatus.canceledAt) {
      const daysLeft = Math.ceil(
        (billingStatus.canceledAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
      )
      return (
        <Badge
          variant="outline"
          className="bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-200 dark:border-yellow-700"
        >
          ⏳ {t('billing.badge.canceledWithAccess', { days: Math.max(0, daysLeft) })}
        </Badge>
      )
    } else {
      return (
        <Badge
          variant="destructive"
          className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900 dark:text-red-200 dark:border-red-700"
        >
          ❌ {t('billing.badge.canceled')}
        </Badge>
      )
    }
  }

  if (billingStatus.subscription) {
    const planLabel =
      billingStatus.subscription.plan === 'annual'
        ? t('billing.badge.annual')
        : t('billing.badge.monthly')

    switch (billingStatus.subscription.status) {
      case 'active':
        if (billingStatus.subscription.cancelAtPeriodEnd) {
          return (
            <Badge
              variant="outline"
              className="bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900 dark:text-orange-200 dark:border-orange-700"
            >
              ⏰ {t('billing.badge.cancelingAtPeriodEnd')}
            </Badge>
          )
        }
        return (
          <Badge
            variant="default"
            className="bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900 dark:text-emerald-200 dark:border-emerald-700"
          >
            💎 {planLabel}
          </Badge>
        )
      case 'trialing':
        return (
          <Badge
            variant="secondary"
            className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-700"
          >
            🎁 {t('billing.badge.trialing')}
          </Badge>
        )
      default:
        return (
          <Badge
            variant="secondary"
            className="bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700"
          >
            💤 {t('billing.badge.inactive')}
          </Badge>
        )
    }
  }

  if (billingStatus.trialEndsAt) {
    const daysLeft = Math.ceil(
      (billingStatus.trialEndsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    )

    if (daysLeft > 0) {
      return (
        <Badge
          variant="outline"
          className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-700"
        >
          🆓{' '}
          {t('billing.badge.trial', {
            days: daysLeft,
            s: daysLeft !== 1 ? 's' : '',
          })}
        </Badge>
      )
    } else {
      return <Badge variant="destructive">⏰ {t('billing.badge.trialExpired')}</Badge>
    }
  }

  return <Badge variant="destructive">🚫 {t('billing.badge.noAccess')}</Badge>
}
